import React, { useEffect, useState } from 'react';
import { Sparkles, Loader2, FileText, CheckCircle2, Copy, Edit3, Save, ChevronDown, Image as ImageIcon, Plus, MessageSquare } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import katex from 'katex';
import { generateExamPaper } from '../services/geminiService';
import { DocumentStyleAnalysis } from '../services/documentService';
import { ExamPaper, Question, SubQuestion } from './types';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db, auth } from '@/firebase/config';
import { DiagramBlock, DiagramSpec } from '../../components/DiagramBlock';
import { renderToStaticMarkup } from 'react-dom/server';

const splitTextByDiagrams = (text: string) => {
  const regex = /(%%DIAGRAM%%.*?%%)/s;
  return text.split(regex).filter(Boolean);
};

type VisualAttachment =
  | { kind: 'diagram'; spec: DiagramSpec }
  | { kind: 'image'; src: string; alt?: string };

const isImageSource = (value?: string | null) =>
  Boolean(value && /^(https?:\/\/|data:image\/|blob:|\/|\.\/|\.\.\/)/i.test(value.trim()));

const markerFromSpec = (spec: unknown) => `%%DIAGRAM%%${JSON.stringify(spec)}%%`;

const normalizeDiagramSpec = (spec: any): DiagramSpec | null => {
  if (!spec?.type) return null;
  const rawType = String(spec.type).trim().toLowerCase().replace(/[,\s]+$/g, '').replace(/_/g, '-');
  const aliases: Record<string, DiagramSpec['type']> = {
    coordinate_plane: 'coordinate-plane',
    cartesian: 'coordinate-plane',
    'cartesian-plane': 'coordinate-plane',
    graph: 'function-graph',
    function: 'function-graph',
    plot: 'function-graph',
    region: 'area-region',
    'area-under-curve': 'area-region',
  };
  const type = aliases[rawType] || rawType;
  return { ...spec, type } as DiagramSpec;
};

const parseDiagramMarker = (value?: string | null): DiagramSpec | null => {
  if (!value) return null;
  const marker = value.match(/%%DIAGRAM%%([\s\S]*?)%%/);
  const raw = marker?.[1] ?? value;
  try {
    const parsed = JSON.parse(raw.trim());
    return normalizeDiagramSpec(parsed);
  } catch {
    return null;
  }
};

const getVisualAttachments = (item: Partial<Question>): VisualAttachment[] => {
  const visuals: VisualAttachment[] = [];
  const maybeImage = item.image?.trim();
  const imageDiagram = parseDiagramMarker(maybeImage);

  const hasInlineDiagram = item.text?.includes('%%DIAGRAM%%');

  if (imageDiagram) {
    if (!hasInlineDiagram) visuals.push({ kind: 'diagram', spec: imageDiagram });
  } else if (isImageSource(maybeImage)) {
    visuals.push({ kind: 'image', src: maybeImage!, alt: 'Question diagram' });
  }

  const diagramPayloads = [
    item.diagram,
    item.diagramSpec,
    ...(Array.isArray(item.diagrams) ? item.diagrams : []),
  ];

  if (!hasInlineDiagram) {
    diagramPayloads.forEach((payload) => {
      const spec = typeof payload === 'string' ? parseDiagramMarker(payload) : payload;
      const normalizedSpec = normalizeDiagramSpec(spec);
      if (normalizedSpec) {
        visuals.push({ kind: 'diagram', spec: normalizedSpec });
      }
    });
  }

  return visuals;
};

const VisualAttachments = ({ item }: { item: Partial<Question> }) => {
  const visuals = getVisualAttachments(item);
  if (visuals.length === 0) return null;

  return (
    <div className="mt-4 space-y-4">
      {visuals.map((visual, index) => (
        <div key={index} className="flex justify-center">
          <div className="max-w-[400px] w-full">
            {visual.kind === 'diagram' ? (
              <DiagramBlock spec={visual.spec} />
            ) : (
              <img
                src={visual.src}
                alt={visual.alt || 'Question diagram'}
                className="w-full h-auto rounded-lg border border-slate-200 dark:border-white/10 bg-white object-contain"
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const renderVisualAttachmentsHtml = (item: Partial<Question>): string =>
  getVisualAttachments(item).map((visual) => {
    if (visual.kind === 'diagram') {
      return `<div style="display:flex;justify-content:center;margin:1rem 0"><div style="max-width:400px;width:100%">${renderToStaticMarkup(<DiagramBlock spec={visual.spec} />)}</div></div>`;
    }
    return `<div style="display:flex;justify-content:center;margin:1rem 0"><img src="${visual.src}" alt="${visual.alt || 'Question diagram'}" style="max-width:400px;width:100%;height:auto;border:1px solid #e2e8f0;border-radius:8px;object-fit:contain" /></div>`;
  }).join('');

const inferDiagramSpecFromText = (text: string): DiagramSpec | null => {
  const normalized = text.replace(/\s+/g, ' ');
  const sqrtXMatch = normalized.match(/y\s*=\s*(?:\\sqrt\{x\}|sqrt\(x\)|sqrt\s*x)/i);
  const expMatch = normalized.match(/y\s*=\s*(?:e\^x|e\{?\^?x\}?|\\?exp\(x\))/i);
  const cubicMatch = normalized.match(/(?:f\(x\)\s*=\s*)?x\^?3\s*-\s*4x/i);
  const parabolaMatch = normalized.match(/y\s*=\s*x\^?2\s*-\s*4/i);
  const verticalLineMatch = normalized.match(/x\s*=\s*(-?\d+(?:\.\d+)?)/i);
  const allVerticalMatches = [...normalized.matchAll(/x\s*=\s*(-?\d+(?:\.\d+)?)/gi)].map((match) => Number(match[1]));
  const mentionsXAxis = /\bx-?axis\b/i.test(normalized);
  const bounds: [number, number] | null = allVerticalMatches.length >= 2
    ? [Math.min(...allVerticalMatches), Math.max(...allVerticalMatches)]
    : verticalLineMatch
      ? [0, Number(verticalLineMatch[1])]
      : null;

  const makeGraph = (equation: string, xBounds: [number, number], caption: string, yRange?: [number, number]): DiagramSpec => ({
    type: 'area-region',
    equation,
    shadeUnderCurve: true,
    shadeRange: { from: xBounds[0], to: xBounds[1] },
    verticalLines: xBounds,
    xRange: [Math.min(-1, xBounds[0] - 1), Math.max(5, xBounds[1] + 1)],
    yRange,
    caption,
  });

  if (sqrtXMatch && verticalLineMatch && mentionsXAxis) {
    const xMax = Number(verticalLineMatch[1]);
    const yMax = Math.max(3, Math.ceil(Math.sqrt(Math.max(xMax, 0))) + 1);
    return makeGraph('y = sqrt(x)', [0, xMax], `Region bounded by y = sqrt(x), the x-axis, and x = ${xMax}`, [0, yMax]);
  }

  if (expMatch && bounds) {
    return makeGraph('y = e^x', bounds, `Region bounded by y = e^x, the x-axis, x = ${bounds[0]}, and x = ${bounds[1]}`, [0, Math.ceil(Math.exp(bounds[1])) + 1]);
  }

  if (parabolaMatch) {
    return makeGraph('y = x^2 - 4', [-2, 2], 'Region enclosed by y = x^2 - 4 and the x-axis', [-5, 2]);
  }

  if (cubicMatch && bounds) {
    return makeGraph('y = x^3 - 4x', bounds, `Graph of y = x^3 - 4x from x = ${bounds[0]} to x = ${bounds[1]}`, [-6, 4]);
  }

  return null;
};

const graphTypes = new Set(['coordinate-plane', 'graph', 'function-graph', 'area-region']);

const diagramHasUsableData = (spec: DiagramSpec) => {
  if (graphTypes.has(spec.type)) {
    const hasEquation = Boolean(spec.equation || spec.expression || spec.function || spec.curve || spec.functions?.length);
    const hasBounds = Boolean(spec.shadeRange || spec.xBounds || spec.verticalLines?.length || spec.xRange);
    return hasEquation && hasBounds;
  }
  return Boolean(spec.points?.length || spec.line || spec.vertices?.length || spec.bars?.length || spec.radius || spec.legs || spec.bearingJourney);
};

const extractDiagramSpecsFromText = (text: string): DiagramSpec[] => {
  const specs: DiagramSpec[] = [];
  text?.replace(/%%DIAGRAM%%([\s\S]*?)%%/g, (_match, rawJson) => {
    try {
      const parsed = normalizeDiagramSpec(JSON.parse(rawJson.trim()));
      if (parsed) specs.push(parsed);
    } catch {
      // Invalid markers are handled elsewhere as parse errors in the UI.
    }
    return _match;
  });
  return specs;
};

const validateDiagramQuestionQuality = (paper: ExamPaper) => {
  const failures: string[] = [];
  paper.sections.forEach((section) => {
    section.questions.forEach((q, qIndex) => {
      const items: Array<{ label: string; item: Question | SubQuestion }> = [
        { label: `Question ${qIndex + 1}`, item: q },
        ...(q.subQuestions || []).map((sq) => ({ label: `Question ${qIndex + 1}${sq.id}`, item: sq })),
      ];

      items.forEach(({ label, item }) => {
        const specs = [
          ...extractDiagramSpecsFromText(item.text),
          ...getVisualAttachments(item).filter((visual) => visual.kind === 'diagram').map((visual) => visual.spec),
        ];
        specs.forEach((spec) => {
          if (!diagramHasUsableData(spec)) {
            failures.push(`${label} has an unusable ${spec.type} diagram with no equation/bounds/data.`);
          }
        });

        const asksAboutDiagram = /\b(region shown|diagram|graph|figure)\b/i.test(item.text);
        const hasSpecificMath = /\b(y\s*=|x\s*=|from\s+\$?-?\d|between\s+\$?-?\d|-?\d+(?:\.\d+)?)\b/i.test(item.text);
        if (asksAboutDiagram && specs.length > 0 && !hasSpecificMath) {
          failures.push(`${label} references a diagram but does not provide enough numeric or equation information.`);
        }
      });
    });
  });
  return failures;
};

const enrichDiagramMarkersInText = (text: string): string => {
  if (!text?.includes('%%DIAGRAM%%')) return text;
  const inferredSpec = inferDiagramSpecFromText(text);
  return text.replace(/%%DIAGRAM%%([\s\S]*?)%%/g, (match, rawJson) => {
    try {
      const parsed = normalizeDiagramSpec(JSON.parse(rawJson.trim()));
      if (!parsed) return match;
      const enriched = inferredSpec && (!diagramHasUsableData(parsed) || parsed.type === 'coordinate-plane')
        ? { ...parsed, ...inferredSpec }
        : parsed;
      return markerFromSpec(enriched);
    } catch {
      return inferredSpec ? markerFromSpec(inferredSpec) : match;
    }
  });
};

const ensureDiagramConnection = (text: string): string => {
  if (!text?.includes('%%DIAGRAM%%')) return text;
  const hasReference = /\b(study|use|consider|refer|shown|graph below|figure below|diagram below|illustrated|region shown)\b/i.test(text);
  if (hasReference) return text;
  return `Study the graph below and use it to answer the question.\n${text.trim()}`;
};

const normalizeQuestionVisuals = <T extends Question | SubQuestion>(item: T, includeDiagrams: boolean = true): T => {
  if (!item?.text) return item;
  
  if (!includeDiagrams) {
    return {
      ...item,
      diagram: undefined,
      diagrams: undefined,
      diagramSpec: undefined,
      text: sanitizeDiagramRefs(item.text.replace(/%%DIAGRAM%%[\s\S]*?%%/g, '')),
    };
  }

  let textWithEnrichedMarkers = ensureDiagramConnection(enrichDiagramMarkersInText(item.text));
  const hasInlineDiagram = textWithEnrichedMarkers.includes('%%DIAGRAM%%');
  const mappedVisuals = getVisualAttachments(item).filter(v => v.kind === 'diagram');
  
  if (!hasInlineDiagram && mappedVisuals.length > 0) {
    textWithEnrichedMarkers = `Study the diagram below and use it to answer the question.\n${textWithEnrichedMarkers.trim()}\n${markerFromSpec(mappedVisuals[0].spec)}`;
  }

  const stillNoDiagram = !textWithEnrichedMarkers.includes('%%DIAGRAM%%');
  const inferredSpec = stillNoDiagram ? inferDiagramSpecFromText(textWithEnrichedMarkers) : null;
  
  const normalizedText = inferredSpec
    ? `Study the graph below and use it to answer the question.\n${textWithEnrichedMarkers.trim()}\n${markerFromSpec(inferredSpec)}`
    : sanitizeDiagramRefs(textWithEnrichedMarkers);
    
  return {
    ...item,
    text: normalizedText,
  };
};

const MarkdownText = ({ children, className }: { children: string, className?: string }) => {
  const textToProcess = typeof children === 'string' ? children : String(children || '');
  const processedText = ensureDiagramConnection(enrichDiagramMarkersInText(textToProcess))
    .replace(/\\\([\s\S]*?\\\)/g, (match) => '$' + match.slice(2, -2) + '$')
    .replace(/\\\[[\s\S]*?\\\]/g, (match) => '$$' + match.slice(2, -2) + '$$');

  const parts = splitTextByDiagrams(processedText);

  return (
    <div className={`markdown-body ${className || ''}`}>
      {parts.map((part, index) => {
        if (part.startsWith('%%DIAGRAM%%') && part.endsWith('%%')) {
          try {
            const jsonStr = part.slice(11, -2).trim();
            const spec = JSON.parse(jsonStr);
            return (
              <div key={index} className="flex justify-center my-4">
                <div className="max-w-[400px] w-full">
                  <DiagramBlock spec={spec} />
                </div>
              </div>
            );
          } catch (e) {
            return <div key={index} className="text-red-500 text-xs my-2">[Diagram Parse Error]</div>;
          }
        }
        return (
          <ReactMarkdown key={index} remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
            {part}
          </ReactMarkdown>
        );
      })}
    </div>
  );
};

const sanitizeDiagramRefs = (text: string): string => {
  if (!text || text.includes('%%DIAGRAM%%')) return text;
  return text
    .replace(/(?:^|[.!?]\s+)(?:The\s+)?(?:diagram|figure|graph|chart|illustration|sketch|drawing)\s+(?:below|above|on\s+the\s+(?:left|right)|provided|given|overleaf|shows?|illustrates?|depicts?|displays?)[^.!?\n]*[.!?]?/gi, '')
    .replace(/(?:^|[.!?]\s+)Refer(?:ence)?\s+to\s+(?:the\s+)?(?:diagram|figure|graph|chart)[^.!?\n]*[.!?]?/gi, '')
    .replace(/(?:^|[.!?]\s+)As\s+shown\s+in\s+(?:the\s+)?(?:diagram|figure|graph|chart)[^.!?\n]*[.!?]?/gi, '')
    .replace(/(?:^|[.!?]\s+)See\s+(?:the\s+)?(?:diagram|figure|graph|chart)[^.!?\n]*[.!?]?/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
};

const processTextWithDiagrams = (text: string, mathFn?: (t: string) => string): string => {
  if (!text) return '';
  const parts = splitTextByDiagrams(text);
  return parts.map(part => {
    if (part.startsWith('%%DIAGRAM%%') && part.endsWith('%%')) {
      try {
        const jsonStr = part.slice(11, -2).trim();
        const spec = JSON.parse(jsonStr);
        return `<div style="display:flex;justify-content:center;margin:1rem 0"><div style="max-width:400px;width:100%">${renderToStaticMarkup(<DiagramBlock spec={spec} />)}</div></div>`;
      } catch {
        return '<span style="color:red;font-size:12px">[Diagram Parse Error]</span>';
      }
    }
    return mathFn ? mathFn(part) : part;
  }).join('');
};

interface AIGeneratorProps {
  mode: 'manual' | 'ai';
  setMode: (mode: 'manual' | 'ai') => void;
  examPaper: ExamPaper | null;
  setExamPaper: (paper: ExamPaper | null) => void;
  onBack: () => void;
  onSaveDraft?: () => void | Promise<void>;
  saving?: boolean;
  initialMeta?: {
    examTitle: string;
    examType: string;
    customExamName: string;
    instructions: string;
  };
  onMetaChange?: (meta: {
    examTitle: string;
    examType: string;
    customExamName: string;
    instructions: string;
  }) => void;
  onOpenChat?: () => void;
}

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export function AIGenerator({ mode, setMode, examPaper, setExamPaper, onBack, onSaveDraft, saving = false, initialMeta, onMetaChange, onOpenChat }: AIGeneratorProps) {
  const navigate = useNavigate();
  const [examTitle, setExamTitle] = useState(initialMeta?.examTitle || 'Midterm Exam');
  const [examType, setExamType] = useState(initialMeta?.examType || 'CAT');
  const [customExamName, setCustomExamName] = useState(initialMeta?.customExamName || '');
  const [documentBased, setDocumentBased] = useState(false);
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentText, setDocumentText] = useState('');
  const [documentProgress, setDocumentProgress] = useState<{ status: string, progress: number } | null>(null);
  const [documentAnalysis, setDocumentAnalysis] = useState<DocumentStyleAnalysis | null>(null);
  const [isSavingStyle, setIsSavingStyle] = useState(false);
  
  const [subject, setSubject] = useState('Mathematics');
  const [topics, setTopics] = useState<string[]>([]);
  const [currentTopic, setCurrentTopic] = useState('');
  const [difficulty, setDifficulty] = useState('Medium');
  const [numSections, setNumSections] = useState(1);
  const [totalMarks, setTotalMarks] = useState(100);
  
  // Sections state
  const [sections, setSections] = useState([
    { id: 1, name: 'Section A', questionType: 'Short Answer', questionStyle: 'Both (Mixed)', numQuestions: 5, marks: 100, numOptions: 4 }
  ]);
  const [enableOptions, setEnableOptions] = useState(false);
  const [isLocalised, setIsLocalised] = useState(false);
  const [includeDiagrams, setIncludeDiagrams] = useState(false);
  const [diagramCount, setDiagramCount] = useState<string>('random');
  const [diagramTopics, setDiagramTopics] = useState<string>('');
  
  const [instructions, setInstructions] = useState(initialMeta?.instructions || '');
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [studentVersion, setStudentVersion] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);

  // Style Tuning State
  const [styleProfiles, setStyleProfiles] = useState<any[]>([]);
  const [selectedStyleId, setSelectedStyleId] = useState<string>('default');

  useEffect(() => {
    const fetchStylesAndSettings = async () => {
      if (!auth.currentUser) return;
      try {
        // Fetch style profiles
        const q = query(
          collection(db, `teaching_workspaces/${auth.currentUser.uid}/styleProfiles`),
          orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(q);
        const styles = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setStyleProfiles(styles);

        // Fetch default style from settings
        const { getDoc, doc } = await import('firebase/firestore');
        const workspaceDoc = await getDoc(doc(db, 'teaching_workspaces', auth.currentUser.uid));
        if (workspaceDoc.exists()) {
          const settings = workspaceDoc.data()?.settings || {};
          if (settings.defaultStyleProfileId) {
            setSelectedStyleId(settings.defaultStyleProfileId);
          }
        }
      } catch (err) {
        console.error("Failed to fetch style profiles and settings", err);
      }
    };
    fetchStylesAndSettings();
  }, []);

  useEffect(() => {
    onMetaChange?.({
      examTitle,
      examType,
      customExamName,
      instructions,
    });
  }, [customExamName, examTitle, examType, instructions, onMetaChange]);

  const handleAddTopic = () => {
    if (currentTopic.trim() && !topics.includes(currentTopic.trim())) {
      setTopics([...topics, currentTopic.trim()]);
      setCurrentTopic('');
      setError(null);
    }
  };

  const handleRemoveTopic = (topicToRemove: string) => {
    setTopics(topics.filter(t => t !== topicToRemove));
  };

  const handleNumSectionsChange = (e: React.ChangeEvent<HTMLSelectElement> | string) => {
    const value = typeof e === 'string' ? e : (e.target as HTMLSelectElement).value;
    const num = parseInt(value) || 1;
    setNumSections(num);
    
    const newSections = [...sections];
    if (num > sections.length) {
      for (let i = sections.length; i < num; i++) {
        newSections.push({
          id: i + 1,
          name: `Section ${String.fromCharCode(65 + i)}`,
          questionType: 'Short Answer',
          questionStyle: 'Both (Mixed)',
          numQuestions: 5,
          marks: 0,
          numOptions: 4
        });
      }
    } else if (num < sections.length) {
      newSections.splice(num);
    }
    setSections(newSections);
    setError(null);
  };

  const updateSection = (index: number, field: string, value: any) => {
    const newSections = [...sections];
    newSections[index] = { ...newSections[index], [field]: value };
    setSections(newSections);
    setError(null);
  };

  const totalSectionMarks = sections.reduce((sum, s) => sum + s.marks, 0);
  const marksMatch = totalSectionMarks === totalMarks;

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!documentBased && (!subject || topics.length === 0)) {
      setError("Please enter a subject and at least one topic.");
      return;
    }
    if (!marksMatch) {
      setError("The sum of section marks must equal the total marks for the exam.");
      return;
    }
    if (documentBased && !documentText) {
      setError("Please upload a document or wait for processing to complete.");
      return;
    }

    setIsGenerating(true);
    setExamPaper(null);
    
    try {
      const activeStyle = styleProfiles.find(s => s.id === selectedStyleId);
      const paper = await generateExamPaper({
        subject: subject || "Document Content",
        topics: topics.length > 0 ? topics : ["Document Content"],
        difficulty,
        instructions,
        isLocalised,
        includeDiagrams,
        diagramCount: includeDiagrams ? diagramCount : undefined,
        diagramTopics: includeDiagrams && diagramTopics.trim() ? diagramTopics.trim() : undefined,
        documentStyleAnalysis: documentBased && documentAnalysis ? documentAnalysis : undefined,
        styleProfileSnapshot: activeStyle ? activeStyle.systemPromptSnapshot : undefined,
        sections: sections.map(s => ({
          name: s.name,
          questionType: s.questionType.toLowerCase().replace(' ', '-'),
          questionStyle: s.questionStyle,
          numQuestions: s.numQuestions,
          marks: s.marks,
          enableOptions,
          numOptions: s.numOptions
        }))
      });
      // Override title with user input
      paper.title = examType === 'CUSTOM' ? customExamName : examTitle;
      for (const section of paper.sections) {
        for (const q of section.questions) {
          Object.assign(q, normalizeQuestionVisuals(q, includeDiagrams));
          if (q.subQuestions) {
            for (const sq of q.subQuestions) {
              Object.assign(sq, normalizeQuestionVisuals(sq, includeDiagrams));
            }
          }
        }
      }
      const diagramQualityFailures = validateDiagramQuestionQuality(paper);
      if (diagramQualityFailures.length > 0) {
        console.warn('[Diagrams] Some diagrams may have sparse data:', diagramQualityFailures);
      }
      setExamPaper(paper);
    } catch (err: any) {
      console.error("Error generating paper:", err);
      setError(err.message || "An error occurred while generating the exam paper. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = () => {
    if (!examPaper) return;
    const text = JSON.stringify(examPaper, null, 2);
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Convert raw $...$ / $$...$$ / \(...\) / \[...\] LaTeX to rendered KaTeX HTML
  const renderMathInHtml = (html: string): string => {
    if (!html) return '';
    let result = html.replace(/\$\$([\s\S]+?)\$\$|\\\[([\s\S]+?)\\\]/g, (_m, g1, g2) => {
      try { return katex.renderToString((g1 ?? g2).trim(), { displayMode: false, throwOnError: false }); }
      catch { return _m; }
    });
    result = result.replace(/(?<!\$)\$(?!\$)([^$\n]+?)\$(?!\$)|\\\(([\s\S]+?)\\\)/g, (_m, g1, g2) => {
      try { return katex.renderToString((g1 ?? g2).trim(), { displayMode: false, throwOnError: false }); }
      catch { return _m; }
    });
    return result;
  };

  // Convert raw $...$ to MathML for MS Word
  const renderMathForWord = (html: string): string => {
    if (!html) return '';
    let result = html.replace(/\$\$([\s\S]+?)\$\$|\\\[([\s\S]+?)\\\]/g, (_m, g1, g2) => {
      try { return katex.renderToString((g1 ?? g2).trim(), { displayMode: true, output: 'mathml', throwOnError: false }); }
      catch { return _m; }
    });
    result = result.replace(/(?<!\$)\$(?!\$)([^$\n]+?)\$(?!\$)|\\\(([\s\S]+?)\\\)/g, (_m, g1, g2) => {
      try { return katex.renderToString((g1 ?? g2).trim(), { displayMode: false, output: 'mathml', throwOnError: false }); }
      catch { return _m; }
    });
    return result;
  };

  // ─── Build printable HTML from examPaper ───
  const buildExamHtml = (includeAnswers: boolean, format: 'web' | 'word' = 'web'): string => {
    if (!examPaper) return '';
    const renderMath = format === 'word' ? renderMathForWord : renderMathInHtml;
    const katexCss = '<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.css">';
    const styles = `
      <style>
        body { font-family: 'Times New Roman', Times, serif; font-size: 11pt; line-height: 1.4; color: #333; margin: 0; padding: 30px; }
        h1 { font-size: 16pt; text-align: center; text-transform: uppercase; margin-bottom: 4px; }
        .meta { text-align: center; font-size: 10pt; color: #555; margin-bottom: 20px; border-bottom: 2px double #333; padding-bottom: 10px; }
        h2 { font-size: 13pt; border-bottom: 1px solid #333; padding-bottom: 4px; margin-top: 24px; }
        .instructions { font-style: italic; font-size: 10pt; color: #555; margin-bottom: 10px; }
        .q { margin-bottom: 14px; page-break-inside: avoid; }
        .q-text { margin-bottom: 4px; line-height: 1.6; }
        ol.opts { margin: 6px 0 6px 24px; padding: 0; }
        ol.opts li { margin-bottom: 5px; line-height: 1.6; }
        .sub-list { margin: 6px 0 6px 28px; padding: 0; list-style: lower-alpha; }
        .sub-list li { margin-bottom: 8px; line-height: 1.6; }
        .answer-block { margin-top: 6px; border-left: 3px solid #cce; padding-left: 10px; background:#f8f9fa; break-inside: avoid; padding-bottom: 5px; }
        .answer-block h4 { font-size: 9pt; font-weight: bold; color: #0056b3; margin: 4px 0 2px; }
        .answer-text { color: #006400; font-size: 10pt; line-height: 1.6; }
        .answer-highlight { color: #d97706; font-weight: 600; }
        .marking-divider { border: none; border-top: 3px double #d00; margin: 40px 0; }
        @media print { body { padding: 15px; } }
      </style>
    `;
    let body = `<h1>${examPaper.title}</h1><div class="meta">${examPaper.title}</div>`;
    examPaper.sections.forEach(section => {
      body += `<h2>${section.name}</h2>`;
      if (section.instructions) body += `<p class="instructions">${renderMath(section.instructions)}</p>`;
      section.questions.forEach((q, idx) => {
        body += `<div class="q"><div class="q-text">${idx + 1}. ${processTextWithDiagrams(q.text, renderMath)} <em>(${q.marks} marks)</em></div>${renderVisualAttachmentsHtml(q)}`;
        if (q.options && q.options.length > 0) {
          body += `<ol class="opts" type="A">${(q.options as string[]).map(o => `<li>${renderMath(o)}</li>`).join('')}</ol>`;
        }
        if (q.subQuestions && q.subQuestions.length > 0) {
          body += `<ol class="sub-list">${q.subQuestions.map(sq =>
            `<li>${processTextWithDiagrams(sq.text, renderMath)}${renderVisualAttachmentsHtml(sq)} <em>(${sq.marks} marks)</em>${
              sq.options && sq.options.length > 0
                ? `<ol type="A" style="margin-top:4px;margin-left:20px">${(sq.options as string[]).map(o => `<li>${renderMath(o)}</li>`).join('')}</ol>`
                : ''
            }</li>`
          ).join('')}</ol>`;
        }
        const isNotNA = (text: string) => {
          if (!text) return false;
          const t = text.replace(/[*_]/g, '').trim().toUpperCase();
          return t !== 'N/A' && t !== 'NONE' && t !== 'NA';
        };
        const validateExps = (exps: any) => Array.isArray(exps) ? exps.filter(e => isNotNA(e)) : [];

        const hasMainAnswer = isNotNA(q.answer);
        const mainExps = validateExps(q.explanation);
        const hasMainExp = mainExps.length > 0;
        
        const subQuestionsWithAnswers = (q.subQuestions || []).map(sq => {
          return {
            ...sq,
            sqHasAns: isNotNA(sq.answer),
            sqExps: validateExps(sq.explanation)
          };
        });
        const hasSubAnswers = subQuestionsWithAnswers.some(sq => sq.sqHasAns || sq.sqExps.length > 0);

        if (includeAnswers && (hasMainAnswer || hasMainExp || hasSubAnswers)) {
          body += `<div class="answer-block"><h4>Answer / Marking Scheme:</h4>`;
          
          let combinedMain = [hasMainAnswer ? `<span class="answer-highlight">${q.answer}</span>` : '', hasMainExp ? mainExps.join('<br/>') : ''].filter(Boolean).join('<br/><br/>');
          if (combinedMain) {
            body += `<div class="answer-text">${renderMath(combinedMain)}</div>`;
          }

          if (subQuestionsWithAnswers.length > 0) {
            const hasAnySubAnswer = subQuestionsWithAnswers.some(sq => sq.sqHasAns || sq.sqExps.length > 0);
            if (hasAnySubAnswer) {
              body += `<ol class="sub-list" style="color:#006400">${subQuestionsWithAnswers.map(sq => {
                let combinedSq = [sq.sqHasAns ? `<span class="answer-highlight">${sq.answer}</span>` : '', sq.sqExps.length > 0 ? sq.sqExps.join('<br/>') : ''].filter(Boolean).join('<br/><br/>');
                return `<li>${renderMath(combinedSq || 'See marking scheme.')}</li>`;
              }).join('')}</ol>`;
            }
          }
          body += `</div>`;
        }
        body += `</div>`;
      });
    });
    return `<html><head><title>${examPaper.title}</title>${katexCss}${styles}</head><body>${body}</body></html>`;
  };

  // ─── Build Inline HTML for TipTap Editor ───
  // Note: We DO NOT call renderMathInHtml here because TipTap strips KaTeX classes.
  // We supply raw $ math $ so the user can edit it and it will be rendered during final export.
  const buildEditorHtml = (includeAnswers: boolean): string => {
    if (!examPaper) return '';
    let body = `<div class="tiptap-editor-area" style="font-family: 'Times New Roman', Times, serif; font-size: 11pt; line-height: 1.4; color: #333; padding: 10px;">`;
    body += `<h1 style="font-size: 16pt; text-align: center; text-transform: uppercase; margin-bottom: 4px;">${examPaper.title}</h1>`;
    body += `<div style="text-align: center; font-size: 10pt; color: #555; margin-bottom: 20px; border-bottom: 2px double #333; padding-bottom: 10px;">${examPaper.title}</div>`;

    examPaper.sections.forEach(section => {
      body += `<h2 style="font-size: 13pt; border-bottom: 1px solid #333; padding-bottom: 4px; margin-top: 24px;">${section.name}</h2>`;
      if (section.instructions) body += `<p style="font-style: italic; font-size: 10pt; color: #555; margin-bottom: 10px;">${section.instructions}</p>`;
      
      section.questions.forEach((q, idx) => {
        body += `<div style="margin-bottom: 14px; page-break-inside: avoid;"><div style="margin-bottom: 4px; line-height: 1.6;"><strong>${idx + 1}.</strong> ${processTextWithDiagrams(q.text)} <em>(${q.marks} marks)</em></div>${renderVisualAttachmentsHtml(q)}`;
        
        if (q.options && q.options.length > 0) {
          body += `<ol type="A" style="margin: 6px 0 6px 24px; padding: 0;">${(q.options as string[]).map(o => `<li style="margin-bottom: 5px; line-height: 1.6;">${o}</li>`).join('')}</ol>`;
        }
        
        if (q.subQuestions && q.subQuestions.length > 0) {
          body += `<ol style="margin: 6px 0 6px 28px; padding: 0; list-style: lower-alpha;">${q.subQuestions.map(sq =>
            `<li style="margin-bottom: 8px; line-height: 1.6;">${processTextWithDiagrams(sq.text)}${renderVisualAttachmentsHtml(sq)} <em>(${sq.marks} marks)</em>${
              sq.options && sq.options.length > 0
                ? `<ol type="A" style="margin-top:4px; margin-left:20px;">${(sq.options as string[]).map(o => `<li style="margin-bottom: 5px; line-height: 1.6;">${o}</li>`).join('')}</ol>`
                : ''
            }</li>`
          ).join('')}</ol>`;
        }
        
        const isNotNA = (text: string) => {
          if (!text) return false;
          const t = text.replace(/[*_]/g, '').trim().toUpperCase();
          return t !== 'N/A' && t !== 'NONE' && t !== 'NA';
        };
        const validateExps = (exps: any) => Array.isArray(exps) ? exps.filter(e => isNotNA(e)) : [];
        const hasMainAnswer = isNotNA(q.answer);
        const mainExps = validateExps(q.explanation);
        const hasMainExp = mainExps.length > 0;
        
        const subQuestionsWithAnswers = (q.subQuestions || []).map(sq => {
          return { ...sq, sqHasAns: isNotNA(sq.answer), sqExps: validateExps(sq.explanation) };
        });
        const hasSubAnswers = subQuestionsWithAnswers.some(sq => sq.sqHasAns || sq.sqExps.length > 0);

        if (includeAnswers && (hasMainAnswer || hasMainExp || hasSubAnswers)) {
          body += `<div style="margin-top: 6px; border-left: 3px solid #cce; padding-left: 10px; background:#f8f9fa; break-inside: avoid; padding-bottom: 5px;">`;
          body += `<h4 style="font-size: 9pt; font-weight: bold; color: #0056b3; margin: 4px 0 2px;">Answer / Marking Scheme:</h4>`;
          
          let combinedMain = [hasMainAnswer ? `<strong style="color: #d97706;">${q.answer}</strong>` : '', hasMainExp ? mainExps.join('<br/>') : ''].filter(Boolean).join('<br/><br/>');
          if (combinedMain) {
            body += `<div style="color: #006400; font-size: 10pt; line-height: 1.6;">${combinedMain}</div>`;
          }

          if (subQuestionsWithAnswers.length > 0) {
            const hasAnySubAnswer = subQuestionsWithAnswers.some(sq => sq.sqHasAns || sq.sqExps.length > 0);
            if (hasAnySubAnswer) {
              body += `<ol style="color:#006400; margin: 6px 0 6px 28px; padding: 0; list-style: lower-alpha;">${subQuestionsWithAnswers.map(sq => {
                let combinedSq = [sq.sqHasAns ? `<strong style="color: #d97706;">${sq.answer}</strong>` : '', sq.sqExps.length > 0 ? sq.sqExps.join('<br/>') : ''].filter(Boolean).join('<br/><br/>');
                return `<li style="margin-bottom: 8px; line-height: 1.6;">${combinedSq || 'See marking scheme.'}</li>`;
              }).join('')}</ol>`;
            }
          }
          body += `</div>`;
        }
        body += `</div>`;
      });
    });
    body += `</div>`;
    return body;
  };

  const handleExportPDF = (withAnswers: boolean) => {
    if (!examPaper) return;
    const html = buildExamHtml(withAnswers, 'web');
    const popup = window.open('', '', 'height=900,width=900');
    if (!popup) { alert('Please allow popups to export as PDF'); return; }
    popup.document.write(html);
    popup.document.close();
    // Wait slightly longer to ensure KaTeX CSS loads via CDN
    setTimeout(() => popup.print(), 1000);
  };

  const handleExportWord = () => {
    if (!examPaper) return;
    const html = buildExamHtml(showAnswers, 'word'); // Uses MathML for Word compatibility
    const wordHtml = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset="utf-8"></head><body>${html.replace('<html>', '').replace('</html>', '').replace(/<head>.*<\/head>/, '')}</body></html>`;
    const source = 'data:application/vnd.ms-word;charset=utf-8,%EF%BB%BF' + encodeURIComponent(wordHtml);
    const a = document.createElement('a');
    a.href = source;
    a.download = `${examPaper.title.replace(/\s+/g, '-')}.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleQuestionEdit = (sectionIndex: number, qIndex: number, updatedQuestion: Question) => {
    if (!examPaper) return;
    const newPaper = { ...examPaper };
    newPaper.sections[sectionIndex].questions[qIndex] = updatedQuestion;
    setExamPaper(newPaper);
    setEditingQuestionId(null);
  };

  return (
    <div className="space-y-6">
      <Card className="print:hidden">
        <CardHeader className="pb-4 border-b border-gray-100 dark:border-white/5 p-2 sm:p-6">
           <div className="flex flex-col sm:flex-row sm:flex-wrap sm:justify-between items-center gap-3">
               <CardTitle className="flex items-center gap-2">
                 <span className="bg-blue-100 text-blue-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0">1</span>
                 Assessment Configuration
               </CardTitle>
               
               <div className="flex items-center justify-center gap-2 bg-gray-100 dark:bg-white/5 p-1 rounded-lg">
                   <button
                      onClick={() => setMode('manual')}
                      className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${mode === 'manual' ? 'bg-white dark:bg-gray-800 shadow text-blue-600' : 'text-muted-foreground hover:text-gray-700'}`}
                   >
                       Manual Entry
                   </button>
                   <button
                      onClick={() => setMode('ai')}
                      className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all flex items-center gap-1.5 ${mode === 'ai' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 shadow' : 'text-muted-foreground hover:text-gray-700'}`}
                   >
                       <Sparkles className="w-3 h-3" /> Generate with AI
                   </button>
               </div>
           </div>
           <CardDescription>Select the type of validity and core details.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 pt-6 animate-in fade-in slide-in-from-top-2 duration-300 px-2 sm:px-6">

        <div className="border border-gray-200 dark:border-white/10 rounded-xl p-2 sm:p-5">
          <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-5">Generate Exam Questions</h3>
          
          <form onSubmit={handleGenerate} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Exam Title</label>
                <input 
                  type="text" 
                  value={examTitle}
                  onChange={(e) => setExamTitle(e.target.value)}
                  className="w-full bg-gray-50 dark:bg-[#0f1221] border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Exam Type</label>
                <div className="relative">
                  <select 
                    value={examType}
                    onChange={(e) => setExamType(e.target.value)}
                    title="Exam type"
                    className="w-full bg-gray-50 dark:bg-[#0f1221] border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 appearance-none focus:outline-none focus:border-blue-500 transition-colors"
                  >
                    <option value="CAT">CAT</option>
                    <option value="MAIN EXAM">MAIN EXAM</option>
                    <option value="CUSTOM">CUSTOM</option>
                  </select>
                  <ChevronDown className="w-3.5 h-3.5 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                </div>
              </div>
            </div>

            {examType === 'CUSTOM' && (
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Custom Exam Name</label>
                <input 
                  type="text" 
                  value={customExamName}
                  onChange={(e) => setCustomExamName(e.target.value)}
                  placeholder="e.g. Mid-Term 2 Mock"
                  className="w-full bg-gray-50 dark:bg-[#0f1221] border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>
            )}

            {/* Language Tuning Dropdown */}
            {!documentBased && (
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Language Tuning (AI Style)</label>
                <div className="relative">
                  <select 
                    value={selectedStyleId}
                    onChange={(e) => setSelectedStyleId(e.target.value)}
                    className="w-full bg-gray-50 dark:bg-[#0f1221] border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 appearance-none focus:outline-none focus:border-blue-500 transition-colors"
                  >
                    <option value="default">Default AI Style (Neutral)</option>
                    {styleProfiles.map(p => (
                      <option key={p.id} value={p.id}>{p.name} ({p.subject})</option>
                    ))}
                  </select>
                  <ChevronDown className="w-3.5 h-3.5 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                </div>
                {styleProfiles.length === 0 && (
                  <p className="text-[10px] text-muted-foreground mt-1.5">
                    You can create personalized Language Tuning profiles in the Assessment tab.
                  </p>
                )}
              </div>
            )}

            <div className="border-t border-b border-gray-200 dark:border-white/10 py-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-xs font-medium text-gray-900 dark:text-gray-100">Style-Learning Upload</span>
                </div>
                <p className="text-[11px] text-muted-foreground">Upload a past paper to learn the teacher's exact writing style, topics, and difficulty level.</p>
              </div>
              <button 
                type="button"
                onClick={() => {
                  setDocumentBased(!documentBased);
                  if (documentBased) {
                    setDocumentFile(null);
                    setDocumentText('');
                    setDocumentProgress(null);
                    setDocumentAnalysis(null);
                  }
                }}
                title="Toggle document-based generation"
                className={`w-9 h-5 rounded-full transition-colors relative ${documentBased ? 'bg-blue-600' : 'bg-[#1e2330]'}`}
              >
                <div className={`w-3.5 h-3.5 rounded-full bg-white absolute top-0.5 transition-transform ${documentBased ? 'left-[18px]' : 'left-0.5'}`} />
              </button>
            </div>

            {documentBased && (
              <div className="bg-blue-50 dark:bg-white/10 border border-gray-200 dark:border-white/20 rounded-xl p-5 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h4 className="text-sm font-bold text-gray-900 dark:text-gray-100">Reference Document</h4>
                  {documentFile && (
                    <button
                      type="button"
                      onClick={() => {
                        setDocumentFile(null);
                        setDocumentText('');
                        setDocumentProgress(null);
                      }}
                      className="text-xs text-red-400 hover:text-red-300 transition-colors"
                    >
                      Remove File
                    </button>
                  )}
                </div>
                
                {!documentFile ? (
                  <div className="border-2 border-dashed border-gray-200 dark:border-white/20 rounded-lg p-8 text-center hover:border-blue-500/50 transition-colors relative">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        if (file.size > 15 * 1024 * 1024) {
                          setError("File size exceeds 15MB limit.");
                          return;
                        }
                        
                        setDocumentFile(file);
                        setError(null);
                        
                        try {
                          const { extractTextFromPDF, analyzeDocumentStyle } = await import('../services/documentService');
                          const result = await extractTextFromPDF(file, (status, progress) => {
                            setDocumentProgress({ status, progress: progress * 0.5 }); // Extraction is first 50%
                          });
                          
                          setDocumentText(result.text);
                          if (result.metadata.title) {
                            setExamTitle(result.metadata.title);
                          }

                          // Phase 2: Style Analysis
                          const analysis = await analyzeDocumentStyle(result.text, (status) => {
                            setDocumentProgress({ status, progress: 75 });
                          });
                          
                          setDocumentAnalysis(analysis);
                          setSubject(analysis.subject);
                          setTopics(analysis.topics);
                          if (['Easy', 'Medium', 'Hard'].includes(analysis.difficulty)) {
                            setDifficulty(analysis.difficulty);
                          }
                          
                          setDocumentProgress({ status: 'Ready! Style extracted.', progress: 100 });
                        } catch (err: any) {
                          console.error("Error extracting text/style:", err);
                          setError("Failed to process document. " + (err.message || ""));
                          setDocumentFile(null);
                          setDocumentProgress(null);
                          setDocumentAnalysis(null);
                        }
                      }}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <FileText className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-1">Click to upload or drag and drop</p>
                    <p className="text-xs text-muted-foreground">PDF only (max 15MB)</p>
                  </div>
                ) : (
                  <div className="bg-gray-50 dark:bg-[#0f1221] border border-gray-200 dark:border-white/10 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{documentFile.name}</p>
                        <p className="text-xs text-muted-foreground">{(documentFile.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                      {documentProgress?.progress === 100 && (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                    
                    {documentProgress && documentProgress.progress < 100 && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">{documentProgress.status}</span>
                          <span className="text-blue-400">{Math.round(documentProgress.progress)}%</span>
                        </div>
                        <div className="h-1.5 w-full bg-[#1e2330] rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 transition-all duration-300 ease-out"
                            style={{ width: `${documentProgress.progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                    
                    {documentAnalysis && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-white/10 space-y-4">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-purple-500" />
                          <h5 className="text-sm font-bold text-gray-900 dark:text-gray-100">Style Analysis Complete</h5>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          <div className="bg-white dark:bg-white/5 rounded-md p-2.5 border border-gray-200 dark:border-white/10">
                            <span className="text-xs text-muted-foreground block mb-1">Subject & Topics</span>
                            <span className="font-semibold text-gray-900 dark:text-gray-100">{documentAnalysis.subject}</span>
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 truncate">{documentAnalysis.topics.join(', ')}</div>
                          </div>
                          <div className="bg-white dark:bg-white/5 rounded-md p-2.5 border border-gray-200 dark:border-white/10">
                            <span className="text-xs text-muted-foreground block mb-1">Tone & Difficulty</span>
                            <span className="font-semibold text-gray-900 dark:text-gray-100">{documentAnalysis.difficulty}</span>
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 truncate">{documentAnalysis.tone}</div>
                          </div>
                        </div>
                        <button
                          type="button"
                          disabled={isSavingStyle}
                          onClick={async () => {
                            if (!auth.currentUser) return;
                            setIsSavingStyle(true);
                            try {
                              const { saveStyleProfile } = await import('../services/styleTunerService');
                              await saveStyleProfile(
                                auth.currentUser.uid,
                                documentAnalysis.suggestedProfileName,
                                documentAnalysis.subject,
                                {
                                  samples: [documentText],
                                  topic: documentAnalysis.topics.join(', '),
                                  previewQuestions: [],
                                  round: 1,
                                  feedbackHistory: [],
                                  fingerprint: {
                                    register: documentAnalysis.tone,
                                    depth: documentAnalysis.difficulty,
                                    preferredVerbs: [],
                                    format: documentAnalysis.sectionStructure,
                                    avgLength: 'Medium',
                                    mathNotation: documentAnalysis.mathStyle,
                                    culturalFlavour: 'Global'
                                  },
                                  systemPromptSnapshot: documentAnalysis.systemPromptSnapshot
                                }
                              );
                              alert('Style profile saved successfully! You can find it in your Style Tuner.');
                            } catch (e: any) {
                              alert('Failed to save style profile: ' + e.message);
                            } finally {
                              setIsSavingStyle(false);
                            }
                          }}
                          className="w-full flex items-center justify-center gap-2 py-2 bg-purple-500/10 hover:bg-purple-500/20 text-purple-600 dark:text-purple-400 font-bold rounded-lg text-xs transition-colors"
                        >
                          {isSavingStyle ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                          Save as Permanent AI Style Profile
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {!documentBased && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                    Subject
                  </label>
                  <input 
                    type="text" 
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full bg-gray-50 dark:bg-[#0f1221] border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:border-blue-500 transition-colors"
                    required={!documentBased}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                    Topic(s)
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input 
                      type="text" 
                      value={currentTopic}
                      onChange={(e) => setCurrentTopic(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTopic();
                        }
                      }}
                      placeholder="Type a topic and press Enter or click Add"
                      className="flex-1 min-w-0 bg-gray-50 dark:bg-[#0f1221] border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-600 focus:outline-none focus:border-blue-500 transition-colors"
                    />
                    <button 
                      type="button" 
                      onClick={handleAddTopic}
                      className="shrink-0 px-3 py-2 bg-gray-50 dark:bg-[#0f1221] border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-gray-100 hover:bg-blue-50 dark:hover:bg-white/10 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  {topics.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {topics.map(t => (
                        <span key={t} className="bg-blue-50 dark:bg-white/10 border border-gray-200 dark:border-white/20 text-gray-700 dark:text-gray-300 px-2.5 py-1 rounded-md text-xs flex items-center gap-1.5">
                          {t}
                          <button 
                            type="button" 
                            onClick={() => handleRemoveTopic(t)}
                            className="text-muted-foreground hover:text-red-400 transition-colors"
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Difficulty</label>
                <div className="relative">
                  <select 
                    value={difficulty}
                    onChange={(e) => setDifficulty(e.target.value)}
                    title="Difficulty"
                    className="w-full bg-gray-50 dark:bg-[#0f1221] border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 appearance-none focus:outline-none focus:border-blue-500 transition-colors"
                  >
                    <option value="Easy">Easy (Primary School)</option>
                    <option value="Medium">Medium (High School)</option>
                    <option value="Hard">Hard (University)</option>
                  </select>
                  <ChevronDown className="w-3.5 h-3.5 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Number of Sections</label>
                <div className="relative">
                  <select 
                    value={numSections}
                    onChange={handleNumSectionsChange}
                    title="Number of sections"
                    className="w-full bg-gray-50 dark:bg-[#0f1221] border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 appearance-none focus:outline-none focus:border-blue-500 transition-colors"
                  >
                    <option value={1}>1 Section</option>
                    <option value={2}>2 Sections</option>
                    <option value={3}>3 Sections</option>
                  </select>
                  <ChevronDown className="w-3.5 h-3.5 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Total Marks for Exam</label>
                <input 
                  type="number" 
                  value={totalMarks}
                  onChange={(e) => setTotalMarks(parseInt(e.target.value) || 0)}
                  className="w-full bg-gray-50 dark:bg-[#0f1221] border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>
            </div>

            {/* Sections Configuration */}
            <div className="space-y-4">
              {sections.map((section, index) => (
                <div key={section.id} className="border border-gray-200 dark:border-white/10 rounded-xl p-4 sm:p-5 bg-gray-50 dark:bg-[#0f1221]">
                  <h4 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-4">{section.name}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-[11px] font-medium text-muted-foreground mb-1.5">Question Type</label>
                      <div className="relative">
                        <select 
                          value={section.questionType}
                          onChange={(e) => updateSection(index, 'questionType', e.target.value)}
                          title={`${section.name} question type`}
                          className="w-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg px-3 py-1.5 text-xs text-gray-900 dark:text-gray-100 appearance-none focus:outline-none focus:border-blue-500 transition-colors"
                        >
                          <option value="Short Answer">Short Answer</option>
                          <option value="Multiple Choice">Multiple Choice</option>
                          <option value="Essay">Essay</option>
                        </select>
                        <ChevronDown className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-muted-foreground mb-1.5">Question Style</label>
                      <div className="relative">
                        <select 
                          value={section.questionStyle}
                          onChange={(e) => updateSection(index, 'questionStyle', e.target.value)}
                          title={`${section.name} question style`}
                          className="w-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg px-3 py-1.5 text-xs text-gray-900 dark:text-gray-100 appearance-none focus:outline-none focus:border-blue-500 transition-colors"
                        >
                          <option value="Both (Mixed)">Both (Mixed)</option>
                          <option value="Single">Single</option>
                          <option value="Sub-questions">Sub-questions</option>
                        </select>
                        <ChevronDown className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-muted-foreground mb-1.5">Number of Questions</label>
                      <input 
                        type="number" 
                        value={section.numQuestions}
                        onChange={(e) => updateSection(index, 'numQuestions', parseInt(e.target.value) || 0)}
                        className="w-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg px-3 py-1.5 text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:border-blue-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-muted-foreground mb-1.5">Section Marks</label>
                      <input 
                        type="number" 
                        value={section.marks}
                        onChange={(e) => updateSection(index, 'marks', parseInt(e.target.value) || 0)}
                        className="w-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg px-3 py-1.5 text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:border-blue-500 transition-colors"
                      />
                    </div>
                  </div>
                  {(enableOptions || section.questionType === 'Multiple Choice') && (
                    <div className="mt-4">
                      <label className="block text-[11px] font-medium text-muted-foreground mb-1.5">Number of Options</label>
                      <input 
                        type="number" 
                        value={section.numOptions}
                        onChange={(e) => updateSection(index, 'numOptions', parseInt(e.target.value) || 0)}
                        className="w-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg px-3 py-1.5 text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:border-blue-500 transition-colors"
                        min={2}
                        max={10}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div>
              <label className="flex items-center gap-2 cursor-pointer mb-2">
                <input 
                  type="checkbox" 
                  checked={enableOptions}
                  onChange={(e) => setEnableOptions(e.target.checked)}
                  className="rounded text-blue-600 focus:ring-blue-500 bg-transparent border-gray-600 w-3.5 h-3.5"
                />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Enable Options (Multiple-Choice Format)</span>
              </label>
              <div className={`border rounded-lg p-2.5 text-right ${marksMatch ? 'bg-[#0a1f16] border-[#103b25]' : 'bg-red-950/30 border-red-900/50'}`}>
                <span className={`text-xs font-medium ${marksMatch ? 'text-[#4ade80]' : 'text-red-400'}`}>
                  Sum of Section Marks: {totalSectionMarks} / {totalMarks}
                  {!marksMatch && ' (Must match Total Marks)'}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Custom Instructions (Optional)</label>
              <textarea 
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="e.g., Focus on real-life biology examples in photosynthesis. Emphasize factorization problems."
                rows={2}
                className="w-full bg-gray-50 dark:bg-[#0f1221] border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-600 focus:outline-none focus:border-blue-500 transition-colors resize-none text-xs"
              />
            </div>


            <div className="flex flex-wrap items-center gap-3 sm:gap-6 mt-2">
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="localise"
                  checked={isLocalised}
                  onChange={(e) => setIsLocalised(e.target.checked)}
                  className="rounded text-blue-600 focus:ring-blue-500 bg-transparent border-gray-600 w-3.5 h-3.5"
                />
                <label htmlFor="localise" className="text-xs font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                  Localise (Use Kenyan names, locations, and context)
                </label>
              </div>
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="includeDiagrams"
                    checked={includeDiagrams}
                    onChange={(e) => setIncludeDiagrams(e.target.checked)}
                    className="rounded text-blue-600 focus:ring-blue-500 bg-transparent border-gray-600 w-3.5 h-3.5"
                  />
                  <label htmlFor="includeDiagrams" className="text-xs font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                    Include Diagrams (At least 1 relevant diagram)
                  </label>
                </div>
                {includeDiagrams && (
                  <div className="ml-5 flex flex-col gap-2 p-2.5 rounded-lg bg-blue-950/20 border border-blue-900/30">
                    <div className="flex items-center gap-2">
                      <label className="text-[11px] text-gray-400 whitespace-nowrap">Number:</label>
                      <select
                        value={diagramCount}
                        onChange={(e) => setDiagramCount(e.target.value)}
                        className="text-[11px] px-2 py-1 rounded bg-gray-800 border border-gray-600 text-gray-200 outline-none flex-1"
                      >
                        <option value="random">Random (min 2)</option>
                        <option value="1">Exactly 1</option>
                        <option value="2">Exactly 2</option>
                        <option value="3">Exactly 3</option>
                        <option value="4">Exactly 4</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-[11px] text-gray-400 whitespace-nowrap">Topics:</label>
                      <input
                        type="text"
                        value={diagramTopics}
                        onChange={(e) => setDiagramTopics(e.target.value)}
                        placeholder="e.g. Integration, Geometry (optional)"
                        className="text-[11px] px-2 py-1 rounded bg-gray-800 border border-gray-600 text-gray-200 placeholder-gray-500 outline-none flex-1"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap justify-end items-center gap-3 sm:gap-4">
              {error && (
                <div className="text-red-400 text-xs font-medium bg-red-950/30 border border-red-900/50 px-3 py-2 rounded-lg flex-1 min-w-[200px]">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={isGenerating || (!documentBased && !subject)}
                className="py-2 px-5 bg-blue-600 text-gray-900 dark:text-gray-100 rounded-lg text-sm font-medium hover:bg-blue-700 focus:ring-4 focus:ring-blue-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shrink-0 w-full sm:w-auto justify-center"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  'Generate Questions'
                )}
              </button>
            </div>
          </form>
        </div>
        </CardContent>
      </Card>

      {/* Output Section */}
      <Card className="overflow-hidden print:overflow-visible print:border-none print:bg-white print:shadow-none">
        {/* Output Toolbar */}
        <CardHeader className="border-b border-gray-100 dark:border-white/5 p-4 flex flex-wrap items-center justify-between gap-2 bg-gray-50/50 dark:bg-white/5 print:hidden space-y-0">
          <CardTitle className="text-base">Exam Preview</CardTitle>
          <div className="flex flex-wrap items-center gap-3">
            {window !== window.top && (
              <span className="text-[10px] text-amber-500/80 hidden lg:inline" title="Printing may be blocked in the preview iframe">
                Open in new tab to print
              </span>
            )}
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input 
                type="checkbox" 
                checked={studentVersion} 
                onChange={(e) => setStudentVersion(e.target.checked)}
                className="rounded text-blue-600 focus:ring-blue-500 bg-transparent border-gray-600 w-3.5 h-3.5"
              />
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Student Version (Spacing & Checkboxes)</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input 
                type="checkbox" 
                checked={showAnswers} 
                onChange={(e) => setShowAnswers(e.target.checked)}
                className="rounded text-blue-600 focus:ring-blue-500 bg-transparent border-gray-600 w-3.5 h-3.5"
              />
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Show Marking Scheme</span>
            </label>
            <button 
              onClick={() => {
                if (examPaper) {
                  const htmlContent = buildEditorHtml(showAnswers);
                  localStorage.setItem('course-outline-draft', JSON.stringify({
                    name: examPaper.title || 'Assessment Draft',
                    data: htmlContent,
                    lastSaved: new Date().toISOString()
                  }));
                  navigate('/dashboard/lessons/course/outline', { 
                    state: { 
                      subjectName: examPaper.title || 'Assessment Draft', 
                      isFirebaseSubject: false,
                      returnPath: '/dashboard/assessment'
                    } 
                  });
                }
              }}
              disabled={!examPaper}
              className="p-1.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-gray-900 dark:text-gray-100 hover:bg-blue-50 dark:bg-white/10"
            >
              <Edit3 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Edit Assessment</span>
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  disabled={!examPaper}
                  title="Export exam as PDF in a new window"
                  className="p-1.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-gray-900 dark:text-gray-100 hover:bg-blue-50 dark:bg-white/10"
                >
                  <FileText className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Export PDF</span>
                  <ChevronDown className="w-3 h-3 hidden sm:inline" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-white/10 shadow-xl rounded-xl p-1">
                <DropdownMenuItem
                  onClick={() => handleExportPDF(false)}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 cursor-pointer"
                >
                  <FileText className="w-4 h-4 text-blue-500" />
                  <div>
                    <div className="font-medium">Questions Only</div>
                    <div className="text-[10px] text-muted-foreground">No answers or marking scheme</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleExportPDF(true)}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 cursor-pointer"
                >
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <div>
                    <div className="font-medium">With Marking Scheme</div>
                    <div className="text-[10px] text-muted-foreground">Includes answers & explanations</div>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              onClick={() => onOpenChat?.()}
              disabled={!examPaper}
              className="p-1.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 text-xs font-bold text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">AI Chat</span>
            </button>
            <Button
              type="button"
              onClick={() => onSaveDraft?.()}
              disabled={!examPaper || saving}
              className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-sm"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save to Dashboard'}
            </Button>
          </div>
        </CardHeader>

        {/* Output Content */}
        <CardContent className="p-1.5 sm:p-3 md:p-4 lg:p-6 flex-1 overflow-y-auto print:p-0 print:overflow-visible print:h-auto print:block print:text-black">
          {isGenerating ? (
            <div className="flex flex-col items-center justify-center space-y-3 py-16">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              <p className="text-xs font-medium animate-pulse text-muted-foreground">
                AI is crafting the exam and marking scheme...
              </p>
            </div>
          ) : examPaper ? (
            <div className="max-w-5xl mx-auto text-gray-200 print:text-black overflow-x-hidden">
              <h1 className="text-xl font-bold text-center mb-6 uppercase tracking-wider text-gray-900 dark:text-gray-100 print:text-black">{examPaper.title}</h1>
              
              {examPaper.sections.map((section, sIndex) => (
                <div key={sIndex} className="mb-10">
                  <h2 className="text-lg font-bold mb-1.5 text-gray-900 dark:text-gray-100 print:text-black">{section.name}</h2>
                  <p className="text-sm italic mb-5 text-muted-foreground print:text-gray-500 dark:text-gray-400">{section.instructions}</p>
                  
                  <div className="space-y-6">
                    {section.questions.map((q, qIndex) => (
                      <div key={q.id} className="p-1.5 sm:p-2 md:p-3 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-[#0f1221] print:border-none print:p-0 print:bg-transparent relative group">
                        

                        {!studentVersion && editingQuestionId === q.id ? (
                          <div className="space-y-3">
                            <div className="flex items-center gap-3">
                              <select
                                value={q.type}
                                onChange={(e) => handleQuestionEdit(sIndex, qIndex, { ...q, type: e.target.value as any })}
                                title="Question type"
                                className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-md px-2 py-1 text-xs text-gray-900 dark:text-gray-100 uppercase font-bold"
                              >
                                <option value="multiple-choice">MULTIPLE CHOICE</option>
                                <option value="short-answer">SHORT ANSWER</option>
                                <option value="essay">ESSAY</option>
                              </select>
                            </div>
                            <textarea 
                              value={q.text}
                              onChange={(e) => handleQuestionEdit(sIndex, qIndex, { ...q, text: e.target.value })}
                              className="w-full p-2 border rounded-md bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 text-gray-900 dark:text-gray-100 text-sm"
                              rows={3}
                            />
                            
                            {q.type === 'multiple-choice' && (!q.subQuestions || q.subQuestions.length === 0) && (
                              <div className="space-y-2 mt-3">
                                <label className="text-xs font-medium text-muted-foreground">Options</label>
                                {(q.options || []).map((opt, oIndex) => {
                                  const isSelected = q.answer === opt;
                                  return (
                                    <div 
                                      key={oIndex} 
                                      className={`flex flex-wrap items-center gap-2 sm:gap-3 p-2 rounded-lg border cursor-pointer transition-colors ${
                                        isSelected 
                                          ? 'bg-green-500/10 border-green-500/50' 
                                          : 'bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 hover:border-gray-600'
                                      }`}
                                      onClick={() => handleQuestionEdit(sIndex, qIndex, { ...q, answer: opt })}
                                    >
                                      <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                                        isSelected ? 'border-green-500 bg-green-500 text-gray-900 dark:text-gray-100' : 'border-gray-600 text-transparent'
                                      }`}>
                                        {isSelected && <CheckCircle2 className="w-3.5 h-3.5" />}
                                      </div>
                                      <span className={`font-bold w-4 ${isSelected ? 'text-green-500' : 'text-muted-foreground'}`}>
                                        {String.fromCharCode(65 + oIndex)}.
                                      </span>
                                      <input 
                                        type="text" 
                                        value={opt}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                          const newOptions = [...(q.options || [])];
                                          newOptions[oIndex] = e.target.value;
                                          // If this was the correct answer, update the answer too
                                          const newAnswer = q.answer === opt ? e.target.value : q.answer;
                                          handleQuestionEdit(sIndex, qIndex, { ...q, options: newOptions, answer: newAnswer });
                                        }}
                                        className="flex-1 bg-transparent text-gray-900 dark:text-gray-100 text-sm focus:outline-none"
                                      />
                                      <button 
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const newOptions = [...(q.options || [])];
                                          newOptions.splice(oIndex, 1);
                                          handleQuestionEdit(sIndex, qIndex, { ...q, options: newOptions });
                                        }}
                                        className="p-1.5 text-red-400 hover:bg-red-950/30 rounded-md"
                                      >
                                        &times;
                                      </button>
                                    </div>
                                  );
                                })}
                                <button 
                                  onClick={() => {
                                    const newOptions = [...(q.options || []), `Option ${(q.options || []).length + 1}`];
                                    handleQuestionEdit(sIndex, qIndex, { ...q, options: newOptions });
                                  }}
                                  className="text-xs text-blue-400 hover:text-blue-300 font-medium mt-1"
                                >
                                  + Add Option
                                </button>
                              </div>
                            )}

                                {q.subQuestions && q.subQuestions.length > 0 && (
                                  <div className="space-y-4 mt-4 border-t border-gray-200 dark:border-white/10 pt-4">
                                    <label className="text-xs font-medium text-muted-foreground">Sub-questions</label>
                                    {q.subQuestions.map((sq, sqIndex) => (
                                      <div key={sqIndex} className="p-3 bg-gray-100 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-white/10 space-y-3 overflow-hidden">
                                        <div className="flex flex-wrap gap-2">
                                          <input 
                                            type="text"
                                            value={sq.id}
                                            onChange={(e) => {
                                              const newSq = [...q.subQuestions!];
                                              newSq[sqIndex] = { ...sq, id: e.target.value };
                                              handleQuestionEdit(sIndex, qIndex, { ...q, subQuestions: newSq });
                                            }}
                                            className="w-12 p-1.5 border rounded-md bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 text-gray-900 dark:text-gray-100 text-sm text-center font-bold shrink-0"
                                            placeholder="a)"
                                          />
                                      <textarea 
                                        value={sq.text}
                                        onChange={(e) => {
                                          const newSq = [...q.subQuestions!];
                                          newSq[sqIndex] = { ...sq, text: e.target.value };
                                          handleQuestionEdit(sIndex, qIndex, { ...q, subQuestions: newSq });
                                        }}
                                        className="flex-1 min-w-[180px] p-1.5 border rounded-md bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 text-gray-900 dark:text-gray-100 text-sm"
                                        rows={2}
                                      />
                                      <select
                                        value={sq.type || 'short-answer'}
                                        onChange={(e) => {
                                          const newSq = [...q.subQuestions!];
                                          newSq[sqIndex] = { ...sq, type: e.target.value as any };
                                          handleQuestionEdit(sIndex, qIndex, { ...q, subQuestions: newSq });
                                        }}
                                        title="Sub-question type"
                                        className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-md px-2 py-1 text-xs text-gray-900 dark:text-gray-100 uppercase font-bold self-start"
                                      >
                                        <option value="multiple-choice">MULTIPLE CHOICE</option>
                                        <option value="short-answer">SHORT ANSWER</option>
                                        <option value="essay">ESSAY</option>
                                      </select>
                                    </div>
                                    
                                    {sq.type === 'multiple-choice' && (
                                      <div className="space-y-2 pl-2 sm:pl-14">
                                        {(sq.options || []).map((opt, oIndex) => {
                                          const isSelected = sq.answer === opt;
                                          return (
                                            <div 
                                              key={oIndex} 
                                              className={`flex flex-wrap items-center gap-2 sm:gap-3 p-2 rounded-lg border cursor-pointer transition-colors ${
                                                isSelected 
                                                  ? 'bg-green-500/10 border-green-500/50' 
                                                  : 'bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 hover:border-gray-600'
                                              }`}
                                              onClick={() => {
                                                const newSq = [...q.subQuestions!];
                                                newSq[sqIndex] = { ...sq, answer: opt };
                                                handleQuestionEdit(sIndex, qIndex, { ...q, subQuestions: newSq });
                                              }}
                                            >
                                              <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                                                isSelected ? 'border-green-500 bg-green-500 text-gray-900 dark:text-gray-100' : 'border-gray-600 text-transparent'
                                              }`}>
                                                {isSelected && <CheckCircle2 className="w-3.5 h-3.5" />}
                                              </div>
                                              <span className={`font-bold w-4 ${isSelected ? 'text-green-500' : 'text-muted-foreground'}`}>
                                                {String.fromCharCode(65 + oIndex)}.
                                              </span>
                                              <input 
                                                type="text" 
                                                value={opt}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) => {
                                                  const newSq = [...q.subQuestions!];
                                                  const newOptions = [...(sq.options || [])];
                                                  newOptions[oIndex] = e.target.value;
                                                  const newAnswer = sq.answer === opt ? e.target.value : sq.answer;
                                                  newSq[sqIndex] = { ...sq, options: newOptions, answer: newAnswer };
                                                  handleQuestionEdit(sIndex, qIndex, { ...q, subQuestions: newSq });
                                                }}
                                                className="flex-1 bg-transparent text-gray-900 dark:text-gray-100 text-sm focus:outline-none"
                                              />
                                              <button 
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  const newSq = [...q.subQuestions!];
                                                  const newOptions = [...(sq.options || [])];
                                                  newOptions.splice(oIndex, 1);
                                                  newSq[sqIndex] = { ...sq, options: newOptions };
                                                  handleQuestionEdit(sIndex, qIndex, { ...q, subQuestions: newSq });
                                                }}
                                                className="p-1.5 text-red-400 hover:bg-red-950/30 rounded-md"
                                              >
                                                &times;
                                              </button>
                                            </div>
                                          );
                                        })}
                                        <button 
                                          onClick={() => {
                                            const newSq = [...q.subQuestions!];
                                            const newOptions = [...(sq.options || []), `Option ${(sq.options || []).length + 1}`];
                                            newSq[sqIndex] = { ...sq, options: newOptions };
                                            handleQuestionEdit(sIndex, qIndex, { ...q, subQuestions: newSq });
                                          }}
                                          className="text-xs text-blue-400 hover:text-blue-300 font-medium mt-1"
                                        >
                                          + Add Option
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            <button 
                              onClick={() => setEditingQuestionId(null)}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-gray-900 dark:text-gray-100 rounded-md text-xs mt-2"
                            >
                              <Save className="w-3.5 h-3.5" /> Save
                            </button>
                          </div>
                        ) : (
                          <div>
                            <div className="flex flex-wrap items-start gap-1.5 sm:gap-2 md:gap-3 text-sm">
                              <span className="font-bold min-w-[1.5rem] text-base sm:text-lg shrink-0">{q.id.replace(/[^0-9]/g, '') || qIndex + 1}.</span>
                              <div className="flex-1 min-w-0 w-full sm:w-auto">
                                <div className="text-sm sm:text-base">
                                  <MarkdownText className="whitespace-pre-wrap">
                                    {q.text}
                                  </MarkdownText>
                                  <VisualAttachments item={q} />
                                </div>
                                
                                {/* Tags - hidden in student mode */}
                                {!studentVersion && (
                                <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-3 print:hidden">
                                  <select
                                    value={q.type}
                                    onChange={(e) => handleQuestionEdit(sIndex, qIndex, { ...q, type: e.target.value as any })}
                                    title="Question type"
                                    className="text-[11px] px-2.5 py-1 rounded-md bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-blue-400 font-medium cursor-pointer outline-none hover:bg-blue-50 dark:hover:bg-white/10 transition-colors"
                                  >
                                    <option value="multiple-choice">MULTIPLE CHOICE</option>
                                    <option value="short-answer">SHORT ANSWER</option>
                                    <option value="essay">ESSAY</option>
                                  </select>
                                  <span className="text-[11px] px-2.5 py-1 rounded-md bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-muted-foreground">{q.difficulty}</span>
                                  <span className="text-[11px] px-2.5 py-1 rounded-md bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-muted-foreground">{q.marks} Marks</span>
                                  {q.topic && <span className="text-[11px] px-2.5 py-1 rounded-md bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-muted-foreground">{q.topic}</span>}
                                </div>
                                )}

                                {/* Options for main question (only if no subquestions) */}
                                {q.type === 'multiple-choice' && (!q.subQuestions || q.subQuestions.length === 0) && q.options && q.options.length > 0 && (
                                  <div className="mt-4 space-y-2 ml-0 sm:ml-2">
                                    {q.options.map((opt, oIndex) => (
                                      <div key={oIndex} className="flex gap-3 text-base items-start">
                                        {studentVersion && <div className="w-4 h-4 mt-1 border border-gray-500 rounded-sm shrink-0 print:border-black"></div>}
                                        <span className="font-bold text-muted-foreground">{String.fromCharCode(65 + oIndex)}.</span>
                                        <MarkdownText className="whitespace-pre-wrap">{opt}</MarkdownText>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Sub-questions */}
                                {q.subQuestions && q.subQuestions.length > 0 && (
                                  <div className="mt-5 space-y-6 ml-0 sm:ml-2">
                                    {q.subQuestions.map(sq => (
                                      <div key={sq.id} className="flex flex-wrap gap-3 text-base">
                                        <span className="font-bold text-muted-foreground">{sq.id.replace(/[()]/g, '')})</span>
                                        <div className="flex-1">
                                          <MarkdownText className="whitespace-pre-wrap">{sq.text}</MarkdownText>
                                          <VisualAttachments item={sq} />
                                          
                                          {/* Sub-question Options */}
                                          {sq.type === 'multiple-choice' && sq.options && sq.options.length > 0 && (
                                            <div className="mt-3 space-y-2">
                                              {sq.options.map((opt, oIndex) => (
                                                <div key={oIndex} className="flex gap-3 text-base items-start">
                                                  {studentVersion && <div className="w-4 h-4 mt-1 border border-gray-500 rounded-sm shrink-0 print:border-black"></div>}
                                                  <span className="font-bold text-muted-foreground">{String.fromCharCode(65 + oIndex)}.</span>
                                                  <MarkdownText className="whitespace-pre-wrap">{opt}</MarkdownText>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                          
                                          {/* Spacing for Student Version (Sub-question) */}
                                          {studentVersion && sq.type !== 'multiple-choice' && (
                                            <div className="mt-8 space-y-8 mb-4">
                                              {Array.from({ length: sq.type === 'essay' ? 8 : 3 }).map((_, i) => (
                                                <div key={i} className="border-b border-gray-600 print:border-gray-400 w-full"></div>
                                              ))}
                                            </div>
                                          )}
                                          
                                          <div className="mt-2 print:hidden">
                                            <select
                                              value={sq.type || 'short-answer'}
                                              onChange={(e) => {
                                                const newSq = [...q.subQuestions!];
                                                const sqIndex = newSq.findIndex(s => s.id === sq.id);
                                                if (sqIndex !== -1) {
                                                  newSq[sqIndex] = { ...sq, type: e.target.value as any };
                                                  handleQuestionEdit(sIndex, qIndex, { ...q, subQuestions: newSq });
                                                }
                                              }}
                                              title="Sub-question type"
                                              className="text-[10px] px-2 py-0.5 rounded-md bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-blue-400 font-medium cursor-pointer outline-none hover:bg-blue-50 dark:hover:bg-white/10 transition-colors"
                                            >
                                              <option value="multiple-choice">MULTIPLE CHOICE</option>
                                              <option value="short-answer">SHORT ANSWER</option>
                                              <option value="essay">ESSAY</option>
                                            </select>
                                          </div>
                                        </div>
                                        <div className="text-right text-sm font-bold text-muted-foreground shrink-0">({sq.marks} marks)</div>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Spacing for Student Version (Main Question) */}
                                {studentVersion && q.type !== 'multiple-choice' && (!q.subQuestions || q.subQuestions.length === 0) && (
                                  <div className="mt-10 space-y-8 mb-6">
                                    {Array.from({ length: q.type === 'essay' ? 12 : 4 }).map((_, i) => (
                                      <div key={i} className="border-b border-gray-600 print:border-gray-400 w-full"></div>
                                    ))}
                                  </div>
                                )}

                              </div>
                              {(!q.subQuestions || q.subQuestions.length === 0) && (
                                <div className="text-xs sm:text-sm font-bold text-muted-foreground w-full sm:w-auto text-right sm:text-left">({q.marks} marks)</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Marking Scheme Section */}
              {showAnswers && (
                <div className="mt-16 pt-10 border-t-2 border-gray-200 dark:border-white/10 print:break-before-page">
                  <h1 className="text-2xl font-bold text-center mb-10 uppercase tracking-wider text-gray-900 dark:text-gray-100 print:text-black">Marking Scheme</h1>
                  
                  {examPaper.sections.map((section, sIndex) => (
                    <div key={`ans-${sIndex}`} className="mb-10">
                      <h2 className="text-xl font-bold mb-6 text-gray-900 dark:text-gray-100 print:text-black">{section.name} - Answers</h2>
                      
                      <div className="space-y-8">
                        {section.questions.map((q, qIndex) => (
                          <div key={`ans-q-${q.id}`} className="bg-gray-50 dark:bg-[#0f1221] border border-gray-200 dark:border-white/10 rounded-xl p-3 sm:p-4 md:p-6 print:border-none print:p-0 print:bg-transparent overflow-hidden">
                            <div className="flex flex-wrap gap-3 sm:gap-4 mb-4">
                              <span className="text-muted-foreground font-bold text-lg">Q{q.id.replace(/[^0-9]/g, '') || qIndex + 1}</span>
                              <div className="flex-1 min-w-[200px]">
                                <MarkdownText className="text-sm text-gray-700 dark:text-gray-300 mb-4 whitespace-pre-wrap">
                                  {q.text}
                                </MarkdownText>
                                <VisualAttachments item={q} />
                                
                                {q.subQuestions && q.subQuestions.length > 0 ? (
                                  <div className="space-y-6">
                                    {q.subQuestions.map(sq => (
                                      <div key={`ans-sq-${sq.id}`} className="border-l-2 border-gray-200 dark:border-white/10 pl-2 sm:pl-4">
                                        <div className="flex flex-wrap justify-between items-start gap-2 mb-3">
                                          <div className="text-sm font-bold text-gray-900 dark:text-gray-100 flex gap-2 min-w-0 flex-1">
                                            <span>{sq.id})</span>
                                            <MarkdownText className="whitespace-pre-wrap">{sq.text}</MarkdownText>
                                            <VisualAttachments item={sq} />
                                          </div>
                                          <div className="text-sm font-bold text-[#f97316] shrink-0">{sq.marks} mks</div>
                                        </div>
                                        
                                    <div className="bg-[#1a1008] border-l-4 border-[#f97316] p-3 sm:p-4 rounded-r-lg mb-4 print:bg-transparent print:border-black overflow-x-auto">
                                          <div className="text-[#f97316] font-medium text-sm flex gap-2 print:text-black">
                                            {sq.type === 'multiple-choice' && sq.options && sq.options.indexOf(sq.answer) !== -1 && (
                                              <span className="font-bold">{String.fromCharCode(65 + sq.options.indexOf(sq.answer))}.</span>
                                            )}
                                            <MarkdownText className="whitespace-pre-wrap">{sq.answer}</MarkdownText>
                                          </div>
                                        </div>
                                        
                                        <div className="space-y-2">
                                          {sq.explanation.map((step, i) => (
                                            <div key={i} className="text-[#4ade80] text-sm font-medium flex gap-2 print:text-gray-800">
                                              <span className="shrink-0">Step {i + 1}:</span>
                                              <MarkdownText className="whitespace-pre-wrap">{step}</MarkdownText>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div>
                                    <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
                                      <div className="text-sm font-bold text-gray-900 dark:text-gray-100">Answer</div>
                                      <div className="text-sm font-bold text-[#f97316]">{q.marks} mks</div>
                                    </div>
                                    
                                        <div className="bg-[#1a1008] border-l-4 border-[#f97316] p-3 sm:p-4 rounded-r-lg mb-4 print:bg-transparent print:border-black overflow-x-auto">
                                      <div className="text-[#f97316] font-medium text-sm flex gap-2 print:text-black">
                                        {q.type === 'multiple-choice' && q.options && q.options.indexOf(q.answer) !== -1 && (
                                          <span className="font-bold">{String.fromCharCode(65 + q.options.indexOf(q.answer))}.</span>
                                        )}
                                        <MarkdownText className="whitespace-pre-wrap">{q.answer}</MarkdownText>
                                      </div>
                                    </div>
                                    
                                    <div className="space-y-2">
                                      {q.explanation.map((step, i) => (
                                        <div key={i} className="text-[#4ade80] text-sm font-medium flex gap-2 print:text-gray-800">
                                          <span className="shrink-0">Step {i + 1}:</span>
                                          <MarkdownText className="whitespace-pre-wrap">{step}</MarkdownText>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center space-y-3 py-16">
              <div className="w-16 h-16 rounded-full flex items-center justify-center bg-blue-50 dark:bg-white/10">
                <FileText className="w-6 h-6 text-muted-foreground" />
              </div>
              <p className="text-xs font-medium text-center max-w-xs text-muted-foreground">
                Configure the exam parameters and click generate to create a structured, editable exam paper.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
